import React from "react";

function renderCard(card, key) {
  return React.createElement(
    "div",
    { className: "centraldoctrine_tarot_cardcontent", key },
    React.createElement(
      "div",
      { className: "centraldoctrine_tarot_cardtext" },

      React.createElement(
        "p",
        { className: "centraldoctrine_tarot_textsm" },
        React.createElement(
          "span",
          { className: "centraldoctrine_tarot_orange" },
          "Mundane Force"
        ),
        React.createElement("br"),
        card.mundaneForce
      ),
      React.createElement(
        "p",
        { className: "centraldoctrine_tarot_textsm" },
        React.createElement(
          "span",
          { className: "centraldoctrine_tarot_orange" },
          "Zodiacal Placement"
        ),
        React.createElement("br"),
        card.zodiacalPlacement
      ),
      React.createElement(
        "p",
        { className: "centraldoctrine_tarot_textsm" },
        React.createElement(
          "span",
          { className: "centraldoctrine_tarot_orange" },
          "Daemonic Spirit"
        ),
        React.createElement("br"),
        card.daemonicSpirit
      )
    ),
    React.createElement("img", {
      className:
        "centraldoctrine_tarot_cardimage centraldoctrine_tarot_cardimg",
      src: card.imageUrl,
      alt: card.title,
      onError: (e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src =
          "https://placehold.co/200x300/1a1a1a/f0e68c?text=Image+Error";
      },
    })
  );
}

function DesktopTable({ data }) {
  const headerCells = (data.headers || []).map((h, i) =>
    React.createElement("th", {
      className: "centraldoctrine_tarot_thtitle",
      key: `h-${i}`,
      scope: "col",
      dangerouslySetInnerHTML: { __html: h },
    })
  );

  const bodyRows = (data.rows || []).map((row, rIdx) => {
    const cardTds = (row.cards || []).map((card, cIdx) =>
      React.createElement("td", { key: `c-${rIdx}-${cIdx}` }, renderCard(card))
    );

    return React.createElement(
      "tr",
      { className: "centraldoctrine_tarot_desktoprow", key: `r-${rIdx}` },
      React.createElement(
        "td",
        { className: "centraldoctrine_tarot_numbercell" },
        React.createElement(
          "span",
          { className: "centraldoctrine_tarot_numberbadge" },
          row.rowNumber ?? ""
        )
      ),
      ...cardTds
    );
  });

  return React.createElement(
    "div",
    { className: "centraldoctrine_tarot_panel" },
    React.createElement(
      "table",
      { className: "centraldoctrine_tarot_table" },
      React.createElement(
        "colgroup",
        null,
        React.createElement("col", null),
        React.createElement("col", null),
        React.createElement("col", null),
        React.createElement("col", null)
      ),
      React.createElement(
        "thead",
        null,
        React.createElement(
          "tr",
          null,
          React.createElement("th", null),
          ...headerCells
        )
      ),
      React.createElement("tbody", null, ...bodyRows)
    )
  );
}

function MobileStack({ data }) {
  const rows = (data.rows || []).map((row, rIdx) => {
    const cardCells = [];

    (row.cards || []).forEach((card, i, arr) => {
      cardCells.push(
        React.createElement(
          "div",
          { className: "centraldoctrine_tarot_cardcell", key: `cell-${i}` },
          renderCard(card)
        )
      );
      if (i < arr.length - 1) {
        cardCells.push(
          React.createElement("div", {
            className: "centraldoctrine_tarot_carddivider",
            key: `div-${i}`,
          })
        );
      }
    });

    return React.createElement(
      "div",
      { className: "centraldoctrine_tarot_rowgroup", key: `rg-${rIdx}` },
      React.createElement(
        "div",
        { className: "centraldoctrine_tarot_rownumber" },
        React.createElement(
          "span",
          { className: "centraldoctrine_tarot_numberbadge" },
          row.rowNumber ?? ""
        )
      ),
      React.createElement(
        "div",
        { className: "centraldoctrine_tarot_cardcol" },
        ...cardCells
      )
    );
  });

  return React.createElement(
    "div",
    { className: "centraldoctrine_tarot_panel" },
    ...rows
  );
}

export default function TarotTables({ tableContent = [] }) {
  // find first item with a main title (to render globally once)
  const firstWithMain = tableContent.find(
    (t) => t && t.hasmainTitle && t.mainTitle
  );
  const hasGlobalMainTitle = Boolean(firstWithMain);

  const globalChildren = [];

  // global H1 + cyan divider (once)
  if (hasGlobalMainTitle) {
    globalChildren.push(
      React.createElement(
        "header",
        { className: "centraldoctrine_tarot_header", key: "global-hdr" },
        React.createElement(
          "h1",
          { className: "centraldoctrine_tarot_title" },
          firstWithMain.mainTitle
        )
      )
    );
  }

  // build all sections (skip section-level mainTitle if global one exists)
  const sectionNodes = tableContent.map((t, idx) => {
    const inner = [];

    // only show a section-level main title if no global one is shown
    if (!hasGlobalMainTitle && t.hasmainTitle && t.mainTitle) {
      inner.push(
        React.createElement(
          "header",
          { className: "centraldoctrine_tarot_header", key: `hdr-${idx}` },
          React.createElement(
            "h1",
            { className: "centraldoctrine_tarot_title" },
            t.mainTitle
          )
        )
      );
    }

    // subtitle (per section)
    if (t.hassubTitle && t.subTitle) {
      inner.push(
        React.createElement(
          "h2",
          { className: "centraldoctrine_tarot_subtitle", key: `sub-${idx}` },
          t.subTitle
        )
      );
    }

    // table title (per section, HTML allowed)
    if (t.hastableTitle && t.tableTitle) {
      inner.push(
        React.createElement("h3", {
          className: "centraldoctrine_tarot_tabletitle",
          key: `tbl-${idx}`,
          dangerouslySetInnerHTML: { __html: t.tableTitle },
        })
      );
    }

    // mobile + desktop tables (per section)
    inner.push(
      React.createElement(
        "div",
        { className: "centraldoctrine_tarot_mobileonly", key: `mob-${idx}` },
        React.createElement(MobileStack, {
          data: t.table || { headers: [], rows: [] },
        })
      )
    );
    inner.push(
      React.createElement(
        "div",
        { className: "centraldoctrine_tarot_desktoponly", key: `desk-${idx}` },
        React.createElement(DesktopTable, {
          data: t.table || { headers: [], rows: [] },
        })
      )
    );

    return React.createElement(
      "section",
      { className: "centraldoctrine_tarot_section", key: `sec-${idx}` },
      ...inner
    );
  });

  // wrap ALL sections in a single wrapper that comes after mainTitle
  const wrappedAllSections = React.createElement(
    "div",
    { className: "centraldoctrine_tarot_tableswrapper", key: "tables-wrap" },
    ...sectionNodes
  );

  return React.createElement(
    "div",
    { className: "centraldoctrine_tarot_root" },
    ...globalChildren,
    wrappedAllSections
  );
}
